/**
 * Asset Type Configuration
 *
 * This file defines all available asset types and their properties.
 * Edit this file to add new asset types or modify existing ones.
 */

/**
 * Platform-specific character limits (soft limits - shown as warnings, not hard limits)
 */
export const PLATFORM_CHARACTER_LIMITS = {
  Meta: {
    primaryText: 80,
    headline: 25,
    description: 25,
  },
  LinkedIn: {
    primaryText: 150,
    headline: 70,
  },
  TikTok: {
    primaryText: 100,
  },
  Snapchat: {
    brand: 25,
    headline: 34,
  },
  Pinterest: {
    headline: 35,
    description: 800,
  },
} as const;

/**
 * Platforms where character limits are hard limits (input is blocked beyond the limit).
 * If a platform/field is listed here, users cannot type beyond the limit.
 * All other limits remain soft (warning only).
 */
export const HARD_LIMIT_PLATFORMS: Record<string, Set<string>> = {
  TikTok: new Set(['primaryText']),
};

/**
 * Special case overrides for specific asset types
 */
const ASSET_TYPE_OVERRIDES: Record<
  string,
  {
    primaryText?: number;
    brand?: number;
    headline?: number;
    description?: number;
  }
> = {
  'meta-unified-collection': {
    headline: 40, // Collection ads use 40 char headlines instead of 25
  },
};

export interface AssetTypeConfig {
  id: string;
  title: string;
  description: string;
  icon: string;
  platforms: string[];

  // Feature flags
  features: {
    /** Whether this asset type uses carousel format (multiple items with headlines/descriptions) */
    isCarousel?: boolean;

    /** Whether this asset type uses collection format (hero + grid items) */
    isCollection?: boolean;

    /** Whether this asset type is Dynamic Creative (multiple copy/asset options) */
    isDynamicCreative?: boolean;

    /** Whether this asset type is Flexible Ad (like Dynamic Creative but no ratio splitting) */
    isFlexibleAd?: boolean;

    /** Whether this asset type supports multiple ratios (1x1 + 9x16) in a single campaign row */
    isUnifiedMeta?: boolean;

    /** Whether this asset type supports description field for single images */
    supportsDescription?: boolean;

    /** Whether this asset type uses 9:16 vertical format */
    is9x16?: boolean;

    /** Whether this asset type supports multiple post copy variations (1-8) */
    supportsMultipleCopies?: boolean;

    /** Whether this asset type uses "Brand" field instead of post copy (Snapchat) */
    usesBrandField?: boolean;

    /** Whether this asset type is text-only (no image/video assets) */
    isTextOnly?: boolean;

    /** Whether this asset type is a document ad (PDF upload) */
    isDocument?: boolean;
  };

  // Default values
  defaults: {
    /** Number of primary text options */
    primaryTextCount: number;

    /** Number of headline options */
    headlineCount: number;

    /** Number of description options */
    descriptionCount: number;

    /** Number of asset file slots */
    assetFileCount: number;

    /** Default CTA text */
    defaultCta: string;

    /** Number of carousel items (if carousel) */
    carouselItemCount?: number;

    /** Maximum number of carousel items (if carousel) */
    maxCarouselItems?: number;

    /** Minimum number of carousel items (if carousel) */
    minCarouselItems?: number;

    /** Number of collection items (if collection) */
    collectionItemCount?: number;
  };
}

/**
 * All available asset types
 *
 * To add a new asset type:
 * 1. Add a new object to this array
 * 2. Set the appropriate feature flags
 * 3. Configure the default values
 */
export const ASSET_TYPES: AssetTypeConfig[] = [
  // ========================================
  // META / FACEBOOK / INSTAGRAM
  // Unified types support multiple ratios (1x1 + 9x16) in a single campaign row
  // ========================================
  {
    id: 'meta-unified-social-ad',
    title: 'Meta Single Image or Video Ad',
    description: 'Feed (1:1 or 4:5) + Stories/Reels (9:16)',
    icon: '+',
    platforms: ['Meta', 'Facebook/Instagram'],
    features: {
      isUnifiedMeta: true,
      supportsDescription: true,
      supportsMultipleCopies: true,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 1,
      descriptionCount: 1,
      assetFileCount: 2, // One per ratio
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'meta-unified-carousel',
    title: 'Meta Carousel (All Placements)',
    description: 'Feed (1:1 or 4:5) + Stories/Reels (9:16) carousel',
    icon: '+',
    platforms: ['Meta', 'Facebook/Instagram'],
    features: {
      isUnifiedMeta: true,
      isCarousel: true,
      supportsMultipleCopies: true,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 3,
      descriptionCount: 3,
      assetFileCount: 6, // 3 per ratio
      defaultCta: 'Learn More',
      carouselItemCount: 3,
    },
  },
  {
    id: 'meta-unified-dynamic-creative',
    title: 'Meta Dynamic Creative (All Placements)',
    description: 'Feed (1:1 or 4:5) + Stories/Reels (9:16) dynamic',
    icon: '+',
    platforms: ['Meta', 'Facebook/Instagram'],
    features: {
      isUnifiedMeta: true,
      isDynamicCreative: true,
    },
    defaults: {
      primaryTextCount: 10, // CAT-1278: increased from 5
      headlineCount: 10, // CAT-1278: increased from 5
      descriptionCount: 10, // CAT-1278: increased from 5
      assetFileCount: 10, // 10 options per ratio (CAT-1278: increased from 5)
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'meta-unified-collection',
    title: 'Meta Collection (All Placements)',
    description: 'Feed (1:1 or 4:5) + Stories/Reels (9:16) collection',
    icon: '+',
    platforms: ['Meta', 'Facebook/Instagram'],
    features: {
      isUnifiedMeta: true,
      isCollection: true,
      supportsMultipleCopies: true,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 4,
      descriptionCount: 4,
      assetFileCount: 8, // 4 per ratio
      defaultCta: 'Learn More',
      collectionItemCount: 4,
    },
  },
  {
    id: 'meta-flexible-ad',
    title: 'Meta Flexible Ad',
    description: 'Flexible ratios, multiple options',
    icon: '+',
    platforms: ['Meta', 'Facebook/Instagram'],
    features: {
      isFlexibleAd: true,
    },
    defaults: {
      primaryTextCount: 10, // CAT-1278: increased from 5
      headlineCount: 10, // CAT-1278: increased from 5
      descriptionCount: 10, // CAT-1278: increased from 5
      assetFileCount: 10, // 10 options (CAT-1278: increased from 5)
      defaultCta: 'Learn More',
    },
  },

  // ========================================
  // LINKEDIN
  // ========================================
  {
    id: 'linkedin-1x1-social-ad',
    title: 'LinkedIn 1:1 Single Image or Video Ad',
    description: 'Single image/video post',
    icon: '+',
    platforms: ['LinkedIn'],
    features: {
      is9x16: false,
      supportsMultipleCopies: true,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 1,
      descriptionCount: 0,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'linkedin-1x1-carousel',
    title: 'LinkedIn 1:1 Carousel',
    description: 'Multiple items carousel',
    icon: '+',
    platforms: ['LinkedIn'],
    features: {
      isCarousel: true,
      is9x16: false,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 3,
      descriptionCount: 0,
      assetFileCount: 3,
      defaultCta: 'Learn More',
      carouselItemCount: 3,
    },
  },
  {
    id: 'linkedin-document',
    title: 'LinkedIn Document Ad',
    description: 'PDF document ad (max 100 MB)',
    icon: '+',
    platforms: ['LinkedIn'],
    features: {
      isDocument: true,
      is9x16: false,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 1,
      descriptionCount: 0,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },

  // ========================================
  // TIKTOK
  // ========================================
  {
    id: 'tiktok-ad',
    title: 'TikTok Ad',
    description: '9:16 vertical video',
    icon: '+',
    platforms: ['TikTok', 'Tik Tok (Douyin)'],
    features: {
      is9x16: true,
      supportsMultipleCopies: true,
    },
    defaults: {
      primaryTextCount: 1,
      headlineCount: 0,
      descriptionCount: 0,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },

  // ========================================
  // SNAPCHAT
  // ========================================
  {
    id: 'snapchat-9x16-single-image',
    title: 'Snapchat 9:16 Single Image or Video Ad',
    description: 'Vertical image',
    icon: '+',
    platforms: ['Snapchat'],
    features: {
      is9x16: true,
      usesBrandField: true,
    },
    defaults: {
      primaryTextCount: 0,
      headlineCount: 1,
      descriptionCount: 0,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'snapchat-9x16-story',
    title: 'Snapchat 9:16 Story Ad',
    description: 'Vertical story carousel (3-20 tiles)',
    icon: '+',
    platforms: ['Snapchat'],
    features: {
      is9x16: true,
      usesBrandField: true,
      isCarousel: true,
    },
    defaults: {
      primaryTextCount: 0,
      headlineCount: 1,
      descriptionCount: 0,
      assetFileCount: 1,
      defaultCta: 'Shop Now',
      carouselItemCount: 3,
      minCarouselItems: 3,
      maxCarouselItems: 20,
    },
  },

  // ========================================
  // PINTEREST
  // ========================================
  {
    id: 'pinterest-2x3-image',
    title: 'Pinterest 2:3 Standard Image Ad',
    description: 'Vertical image',
    icon: '+',
    platforms: ['Pinterest'],
    features: {
      is9x16: false,
      supportsDescription: true,
    },
    defaults: {
      primaryTextCount: 0,
      headlineCount: 1,
      descriptionCount: 1,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'pinterest-2x3-video',
    title: 'Pinterest 2:3 Standard Video Ad',
    description: 'Vertical video',
    icon: '+',
    platforms: ['Pinterest'],
    features: {
      is9x16: false,
      supportsDescription: true,
    },
    defaults: {
      primaryTextCount: 0,
      headlineCount: 1,
      descriptionCount: 1,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'pinterest-1x1-maxwidth-video',
    title: 'Pinterest 1:1 Max Width Video Ad',
    description: 'Square video',
    icon: '+',
    platforms: ['Pinterest'],
    features: {
      is9x16: false,
      supportsDescription: true,
    },
    defaults: {
      primaryTextCount: 0,
      headlineCount: 1,
      descriptionCount: 1,
      assetFileCount: 1,
      defaultCta: 'Learn More',
    },
  },
  {
    id: 'pinterest-2x3-carousel',
    title: 'Pinterest 2:3 Carousel Ad',
    description: 'Multiple items',
    icon: '+',
    platforms: ['Pinterest'],
    features: {
      isCarousel: true,
      is9x16: false,
    },
    defaults: {
      primaryTextCount: 0,
      headlineCount: 3,
      descriptionCount: 3,
      assetFileCount: 3,
      defaultCta: 'Learn More',
      carouselItemCount: 3,
    },
  },
];

/**
 * Helper function to get asset type configuration by ID
 */
export function getAssetTypeConfig(assetTypeId: string): AssetTypeConfig | undefined {
  return ASSET_TYPES.find((type) => type.id === assetTypeId);
}

/**
 * Helper function to get asset types filtered by platform
 */
export function getAssetTypesForPlatform(platform: string): AssetTypeConfig[] {
  return ASSET_TYPES.filter((assetType) =>
    assetType.platforms.some(
      (p) =>
        platform.toLowerCase().includes(p.toLowerCase()) ||
        p.toLowerCase().includes(platform.toLowerCase()),
    ),
  );
}

/**
 * Maps campaign format values to the feature flag that identifies that format's asset types.
 * Add a new entry here when adding a new special format type — the filter logic needs no changes.
 * Formats not listed here (e.g. "image", "video") are treated as standard single-asset types,
 * meaning any asset type that has none of these feature flags set.
 */
const FORMAT_FEATURE_MAP: Partial<Record<string, keyof AssetTypeConfig['features']>> = {
  carousel: 'isCarousel',
  collection: 'isCollection',
  'dynamic creative': 'isDynamicCreative',
  'flexible ad': 'isFlexibleAd',
  document: 'isDocument',
};

/**
 * Helper function to get asset types filtered by platform, size, and format type
 * @param platform - Campaign platform (e.g., "Meta", "TikTok")
 * @param size - Campaign size (e.g., "9x16", "1x1", "1x1, 9x16")
 * @param formatType - Campaign format type (e.g., "Dynamic Creative", "Carousel", "Video", "Image")
 * @returns Asset types filtered by platform, size, and format type
 */
export function getAssetTypesForPlatformAndSize(
  platform: string,
  size?: string | null,
  formatType?: string | null,
): AssetTypeConfig[] {
  let platformTypes = getAssetTypesForPlatform(platform);

  // For Meta platform, only show unified asset types (with tabs for 1:1 and 9:16) plus flexible ad
  // Hide the individual split types (meta-1x1-*, meta-9x16-*) from the selector
  // Note: Backend normalizes Facebook/Instagram to "Meta", but we include all for defense-in-depth
  const platformLower = platform.toLowerCase();
  const isMeta =
    platformLower.includes('meta') ||
    platformLower.includes('facebook') ||
    platformLower.includes('instagram');
  if (isMeta) {
    platformTypes = platformTypes.filter(
      (assetType) =>
        assetType.features.isUnifiedMeta === true || assetType.features.isFlexibleAd === true,
    );
  }

  // Filter by format type first (most restrictive)
  if (formatType) {
    const normalizedFormat = formatType.toLowerCase().trim();
    const featureFlag = FORMAT_FEATURE_MAP[normalizedFormat];

    if (featureFlag) {
      // Known special format — show only asset types with that feature flag
      platformTypes = platformTypes.filter((assetType) => assetType.features[featureFlag] === true);
    } else if (normalizedFormat === 'story ad') {
      // Story Ad format - match asset types with "story" in their ID
      platformTypes = platformTypes.filter((assetType) => assetType.id.includes('story'));
    } else {
      // image, video, or any unrecognised format — show types with no special format flag set
      const specialFlags = Object.values(
        FORMAT_FEATURE_MAP,
      ) as (keyof AssetTypeConfig['features'])[];
      platformTypes = platformTypes.filter((assetType) =>
        specialFlags.every((flag) => !assetType.features[flag]),
      );
    }

    // If no matches found after format filtering, return empty array (strict filtering)
    if (platformTypes.length === 0) {
      return [];
    }
  }

  // Then filter by size (if provided)
  if (!size) {
    return platformTypes;
  }

  // Normalize size and check for 9:16 format
  const normalizedSize = size.toLowerCase().replace(/\s/g, '');
  const is9x16 = normalizedSize.includes('9x16') || normalizedSize.includes('9:16');
  const is1x1 =
    normalizedSize.includes('1x1') ||
    normalizedSize.includes('1:1') ||
    normalizedSize.includes('4x5') ||
    normalizedSize.includes('4:5');

  // If size contains BOTH 1x1 and 9x16 (combined/unified Meta), show only unified asset types
  if (is1x1 && is9x16) {
    const unified = platformTypes.filter((assetType) => assetType.features.isUnifiedMeta === true);
    // If we found unified asset types, return them. Otherwise, fall back to all platform types.
    return unified.length > 0 ? unified : platformTypes;
  }

  // If size contains only 9x16, filter to only 9x16 asset types
  if (is9x16 && !is1x1) {
    const filtered = platformTypes.filter((assetType) => assetType.features.is9x16 === true);
    // If we found matching asset types, return them. Otherwise, return all platform types.
    return filtered.length > 0 ? filtered : platformTypes;
  }

  // If size contains only 1x1, filter to only 1x1 asset types
  // Use !== true to include both false and undefined (e.g., LinkedIn articles)
  if (is1x1 && !is9x16) {
    const filtered = platformTypes.filter((assetType) => assetType.features.is9x16 !== true);
    // If we found matching asset types, return them. Otherwise, return all platform types.
    return filtered.length > 0 ? filtered : platformTypes;
  }

  // If size contains neither, return all platform types
  return platformTypes;
}

/**
 * Helper function to check if an asset type has a specific feature
 */
export function hasFeature(
  assetTypeId: string,
  feature: keyof AssetTypeConfig['features'],
): boolean {
  const config = getAssetTypeConfig(assetTypeId);
  return config?.features[feature] ?? false;
}

/**
 * Helper function to get character limit for a specific field
 * Uses platform-level defaults with optional asset-type-specific overrides
 */
export function getCharacterLimit(
  assetTypeId: string,
  field: 'primaryText' | 'brand' | 'headline' | 'description',
): number | undefined {
  const config = getAssetTypeConfig(assetTypeId);
  if (!config) return undefined;

  // Check for asset-type-specific override first
  const override = ASSET_TYPE_OVERRIDES[assetTypeId];
  if (override && field in override) {
    return override[field as keyof typeof override];
  }

  // Get platform name (first platform in the array)
  const platformName = config.platforms[0] as keyof typeof PLATFORM_CHARACTER_LIMITS;
  const platformLimits = PLATFORM_CHARACTER_LIMITS[platformName];

  if (!platformLimits) return undefined;

  return platformLimits[field as keyof typeof platformLimits];
}

/**
 * Helper function to check if a character limit is a hard limit (blocks input)
 * Returns true if the platform/field combination is in HARD_LIMIT_PLATFORMS
 */
export function isHardCharacterLimit(
  assetTypeId: string,
  field: 'primaryText' | 'brand' | 'headline' | 'description',
): boolean {
  const config = getAssetTypeConfig(assetTypeId);
  if (!config) return false;

  for (const platformName of config.platforms) {
    const hardFields = HARD_LIMIT_PLATFORMS[platformName];
    if (hardFields?.has(field)) {
      return true;
    }
  }
  return false;
}
